import type { RefObject } from 'preact'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { useBlobUrl } from '~/hooks/useBlobUrl'
import type { PhotoRecord, PhotoView } from '~/models/photo'
import { PHOTO_VIEW_LABELS } from '~/models/photo'
import styles from './PhotoCapture.module.css'

/**
 * Guided capture for one view.
 *
 * **Two paths into the same pipeline, and the fallback is not a lesser one.**
 * A live camera gives the alignment guide, the ghost overlay and the countdown
 * that make two photos comparable. Where `getUserMedia` is unavailable or
 * declined — an older device, a denied permission, a browser that withholds it
 * from an installed app — the file picker still produces a photo, and the
 * record says which path it came from so a later comparison is not read as more
 * like-for-like than it is.
 *
 * **The camera is stopped on every exit path.** A live `MediaStream` left
 * running holds the camera indicator on and drains the battery; iOS is
 * particularly unforgiving about a stream surviving a backgrounded page.
 */

interface PhotoCaptureProps {
  readonly view: PhotoView
  /** The most recent photo of this view, drawn as the ghost overlay. */
  readonly ghost: PhotoRecord | undefined
  readonly onCaptured: (file: Blob, source: PhotoRecord['source']) => void
  readonly onCancel: () => void
  readonly busy: boolean
}

/**
 * Long enough to get back into position after tapping, short enough not to feel
 * like a wait. Holding a pose is the point of the countdown.
 */
const COUNTDOWN_SECONDS = 3

/**
 * Tick a countdown to zero, then fire.
 *
 * Extracted so the component's own complexity stays readable (D-056). The timer
 * is cleared on every change, so cancelling or unmounting mid-count cannot
 * leave a shutter to fire into a torn-down component.
 */
function useCountdown(
  value: number | undefined,
  setValue: (next: number | undefined) => void,
  onFire: () => void,
): void {
  useEffect(() => {
    if (value === undefined) return
    if (value === 0) {
      setValue(undefined)
      onFire()
      return
    }
    const timer = setTimeout(() => setValue(value - 1), 1000)
    return () => clearTimeout(timer)
  }, [value, setValue, onFire])
}

type CameraState =
  | { readonly status: 'idle' }
  | { readonly status: 'starting' }
  | { readonly status: 'live' }
  | { readonly status: 'unavailable'; readonly reason: string }

export function PhotoCapture({ view, ghost, onCaptured, onCancel, busy }: PhotoCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [camera, setCamera] = useState<CameraState>({ status: 'idle' })
  const [countdown, setCountdown] = useState<number | undefined>(undefined)
  const [showGhost, setShowGhost] = useState(true)

  const ghostUrl = useBlobUrl(ghost?.blobId)

  /* Announce the screen change by moving focus to its heading. */
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  const stopCamera = useCallback(() => {
    for (const track of streamRef.current?.getTracks() ?? []) track.stop()
    streamRef.current = null
    if (videoRef.current !== null) videoRef.current.srcObject = null
  }, [])

  /* Stop on unmount, whatever the exit route — cancel, capture, or navigation. */
  useEffect(() => stopCamera, [stopCamera])

  const startCamera = useCallback(async () => {
    if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia === undefined) {
      setCamera({
        status: 'unavailable',
        reason: 'This browser will not give Project Sola a camera preview.',
      })
      return
    }

    setCamera({ status: 'starting' })
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1920 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current !== null) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => undefined)
      }
      setCamera({ status: 'live' })
    } catch {
      /* A denied permission and an absent camera are the same outcome here: no
         preview. The picker below still works, so this is not a dead end. */
      setCamera({
        status: 'unavailable',
        reason: 'Project Sola does not have access to the camera.',
      })
    }
  }, [])

  const grabFrame = useCallback(() => {
    const video = videoRef.current
    if (video === null || video.videoWidth === 0) return

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext('2d')
    if (context === null) return
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(
      (blob) => {
        canvas.width = 0
        canvas.height = 0
        if (blob !== null) onCaptured(blob, 'guidedCapture')
      },
      'image/jpeg',
      /* Grabbed at high quality; the pipeline re-encodes to the stored size, so
         this only needs to avoid throwing detail away before it gets there. */
      0.95,
    )
  }, [onCaptured])

  const startCountdown = useCallback(() => setCountdown(COUNTDOWN_SECONDS), [])
  useCountdown(countdown, setCountdown, grabFrame)

  const onFilePicked = useCallback(
    (event: Event) => {
      const input = event.currentTarget as HTMLInputElement
      const file = input.files?.[0]
      /* Clear the input so picking the same file twice still fires a change. */
      input.value = ''
      if (file !== undefined) onCaptured(file, 'pickedFile')
    },
    [onCaptured],
  )

  return (
    <section class={styles.capture} aria-label={`Capture a ${PHOTO_VIEW_LABELS[view]} photo`}>
      {/* Focused on arrival. Swapping the whole screen leaves focus on a
          button that no longer exists, so a screen-reader or keyboard user is
          dropped at the document root with no announcement that anything
          happened. `tabIndex={-1}` makes the heading focusable without adding
          it to the tab order. */}
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: focus target on screen change */}
      <h2 class={styles.heading} ref={headingRef} tabIndex={-1}>
        {PHOTO_VIEW_LABELS[view]} photo
      </h2>

      <CaptureStage
        view={view}
        camera={camera}
        countdown={countdown}
        ghostUrl={showGhost ? ghostUrl : undefined}
        videoRef={videoRef}
      />

      <div class={styles.controls}>
        {camera.status === 'live' ? (
          <>
            <Button
              onClick={startCountdown}
              disabled={busy || countdown !== undefined}
              fullWidth
              label={`Take the ${PHOTO_VIEW_LABELS[view].toLowerCase()} photo after a countdown`}
            >
              {countdown === undefined ? 'Start countdown' : 'Counting down…'}
            </Button>
            {ghost !== undefined ? (
              <label class={styles.toggle}>
                <input
                  type="checkbox"
                  checked={showGhost}
                  onChange={() => setShowGhost((value) => !value)}
                />
                Show the previous photo behind the preview
              </label>
            ) : null}
          </>
        ) : (
          <Button
            onClick={() => void startCamera()}
            busy={camera.status === 'starting'}
            busyLabel="Opening the camera…"
            disabled={busy || camera.status === 'unavailable'}
            fullWidth
          >
            Open the camera
          </Button>
        )}

        <label class={styles.picker}>
          <span class={styles.pickerLabel}>Or choose a photo you already have</span>
          <input type="file" accept="image/*" onChange={onFilePicked} disabled={busy} />
        </label>

        <Button onClick={onCancel} variant="secondary" disabled={busy} fullWidth>
          Cancel
        </Button>
      </div>

      <p class={styles.privacy}>
        Project Sola re-saves every photo on this device before storing it, which removes the
        location and camera information the file arrived with. Photos never leave the app unless you
        export them yourself.
      </p>
    </section>
  )
}

/**
 * The preview area: placeholder, live frame, ghost, guide and countdown.
 *
 * Extracted from the component above to keep its complexity readable (D-056).
 * The `<video>` element is rendered in every state and hidden by class rather
 * than swapped in and out, because remounting it would drop the `srcObject` the
 * stream is attached to and the preview would go black on the first re-render.
 */
function CaptureStage({
  view,
  camera,
  countdown,
  ghostUrl,
  videoRef,
}: {
  readonly view: PhotoView
  readonly camera: CameraState
  readonly countdown: number | undefined
  readonly ghostUrl: string | undefined
  readonly videoRef: RefObject<HTMLVideoElement>
}) {
  const live = camera.status === 'live'

  return (
    <div class={styles.stage}>
      {live ? null : (
        <div class={styles.placeholder}>
          <p class={styles.placeholderText}>
            {camera.status === 'unavailable'
              ? `${camera.reason} You can still choose a photo you have already taken — it goes through exactly the same processing.`
              : `The preview shows an outline to line yourself up with, and the last ${PHOTO_VIEW_LABELS[view].toLowerCase()} photo faintly behind it.`}
          </p>
        </div>
      )}

      {/* Muted and inline are both required for autoplay on iOS. */}
      <video
        ref={videoRef}
        class={live ? styles.video : styles.videoHidden}
        playsInline
        muted
        aria-label="Camera preview"
      />

      {live && ghostUrl !== undefined ? (
        <img src={ghostUrl} alt="" aria-hidden="true" class={styles.ghost} />
      ) : null}

      {live ? <AlignmentGuide /> : null}

      {countdown === undefined ? null : (
        <div class={styles.countdown} role="status" aria-live="assertive">
          {countdown === 0 ? 'Now' : countdown}
        </div>
      )}
    </div>
  )
}

/**
 * The alignment outline.
 *
 * Deliberately a plain frame with centre and foot markers rather than a body
 * silhouette: an outline shaped like a particular body invites the user to
 * match it, and `ui-ux-spec.md` forbids anything that reshapes or idealises the
 * body. This helps them stand in the same place twice, which is all it should
 * do.
 */
function AlignmentGuide() {
  return (
    <svg class={styles.guide} viewBox="0 0 100 150" aria-hidden="true" focusable="false">
      <title>Alignment guide</title>
      <line x1="50" y1="4" x2="50" y2="146" class={styles.guideCentre} />
      <line x1="20" y1="138" x2="80" y2="138" class={styles.guideFeet} />
      <line x1="20" y1="26" x2="80" y2="26" class={styles.guideHead} />
      <rect x="14" y="8" width="72" height="134" rx="4" class={styles.guideFrame} />
    </svg>
  )
}
